import type { MembershipIncome } from '@fruitful/member-profile';
import React, { type Dispatch, type SetStateAction } from 'react';
import {
    DollarInput,
    H6Emphasized,
    Input,
    LabelRegular,
    MultilineInput,
    Picker,
    Trash2Icon,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import type {
    IncomeDetailsFormState,
    IncomeTypePickerItem,
    OwnerPickerItem,
    PayCyclePickerItem,
} from './moneyMapperIncomeForm';

export interface MoneyMapperIncomeDetailsModalProps {
    isOpen: boolean;
    title: string;
    detailsIncome: MembershipIncome | null;
    detailsModalDraft: IncomeDetailsFormState | null;
    setDetailsModalDraft: Dispatch<
        SetStateAction<IncomeDetailsFormState | null>
    >;
    ownerPickerItems: OwnerPickerItem[];
    payCyclePickerItems: PayCyclePickerItem[];
    incomeTypePickerItems: IncomeTypePickerItem[];
    detailsModalDisabled: boolean;
    canMutateIncomes: boolean;
    isSubmittingIncome: boolean;
    onClose: () => void;
    onSave: () => void;
    onDelete: () => void;
}

export const MoneyMapperIncomeDetailsModal = ({
    isOpen,
    title,
    detailsIncome,
    detailsModalDraft,
    setDetailsModalDraft,
    ownerPickerItems,
    payCyclePickerItems,
    incomeTypePickerItems,
    detailsModalDisabled,
    canMutateIncomes,
    isSubmittingIncome,
    onClose,
    onSave,
    onDelete,
}: MoneyMapperIncomeDetailsModalProps) => {
    return (
        <GroveSimpleModal isOpen={isOpen} hasCloseButton onClose={onClose}>
            <YStack paddingVertical={24} minWidth={320} maxWidth={600}>
                <H6Emphasized color="$gray1200" paddingHorizontal={16}>
                    {title}
                </H6Emphasized>
                {detailsIncome != null && detailsModalDraft != null && (
                    <>
                        <XStack
                            gap={16}
                            marginTop={16}
                            paddingHorizontal={16}
                            paddingVertical={8}
                            flexWrap="wrap"
                        >
                            <YStack flex={1} minWidth={200} gap={8}>
                                <LabelRegular color="$gray1000">
                                    Member
                                </LabelRegular>
                                <Picker
                                    size="regular"
                                    value={detailsModalDraft.belongsToUserId}
                                    options={ownerPickerItems}
                                    disabled={detailsModalDisabled}
                                    onChange={(v) => {
                                        setDetailsModalDraft((d) =>
                                            d != null
                                                ? {
                                                      ...d,
                                                      belongsToUserId: v,
                                                  }
                                                : d,
                                        );
                                    }}
                                />
                            </YStack>
                            <YStack flex={1} minWidth={200} gap={8}>
                                <LabelRegular color="$gray1000">
                                    Source
                                </LabelRegular>
                                <Input
                                    size="regular"
                                    value={detailsModalDraft.description}
                                    onChangeText={(t) => {
                                        setDetailsModalDraft((d) =>
                                            d != null
                                                ? { ...d, description: t }
                                                : d,
                                        );
                                    }}
                                    placeholder="Source"
                                    disabled={detailsModalDisabled}
                                />
                            </YStack>
                        </XStack>
                        <XStack
                            gap={16}
                            paddingHorizontal={16}
                            paddingVertical={8}
                            flexWrap="wrap"
                        >
                            <YStack flex={1} minWidth={200} gap={8}>
                                <LabelRegular color="$gray1000">
                                    Income type
                                </LabelRegular>
                                <Picker
                                    size="regular"
                                    value={detailsModalDraft.type}
                                    options={incomeTypePickerItems}
                                    disabled={detailsModalDisabled}
                                    onChange={(v) => {
                                        setDetailsModalDraft((d) =>
                                            d != null ? { ...d, type: v } : d,
                                        );
                                    }}
                                />
                            </YStack>
                        </XStack>
                        <XStack
                            gap={16}
                            paddingHorizontal={16}
                            paddingVertical={8}
                            flexWrap="wrap"
                        >
                            <YStack flex={1} minWidth={200} gap={8}>
                                <LabelRegular color="$gray1000">
                                    Amount
                                </LabelRegular>
                                <DollarInput
                                    size="regular"
                                    value={detailsModalDraft.periodAmountStr}
                                    onChangeText={(t) => {
                                        setDetailsModalDraft((d) =>
                                            d != null
                                                ? {
                                                      ...d,
                                                      periodAmountStr: t,
                                                  }
                                                : d,
                                        );
                                    }}
                                    allowDecimals={false}
                                    disabled={detailsModalDisabled}
                                />
                            </YStack>
                            <YStack flex={1} minWidth={200} gap={8}>
                                <LabelRegular color="$gray1000">
                                    Pay frequency
                                </LabelRegular>
                                <Picker
                                    size="regular"
                                    value={detailsModalDraft.payCycle}
                                    options={payCyclePickerItems}
                                    disabled={detailsModalDisabled}
                                    onChange={(v) => {
                                        setDetailsModalDraft((d) =>
                                            d != null
                                                ? { ...d, payCycle: v }
                                                : d,
                                        );
                                    }}
                                />
                            </YStack>
                        </XStack>
                        <YStack gap={8} padding={16}>
                            <LabelRegular color="$gray1000">Note</LabelRegular>
                            <MultilineInput
                                value={detailsModalDraft.notes}
                                onChangeText={(t) => {
                                    setDetailsModalDraft((d) =>
                                        d != null ? { ...d, notes: t } : d,
                                    );
                                }}
                                placeholder="Add your note here"
                                numberOfLines={4}
                                disabled={detailsModalDisabled}
                            />
                        </YStack>
                        <XStack
                            justifyContent="space-between"
                            alignItems="center"
                            paddingHorizontal={16}
                            paddingVertical={8}
                        >
                            <Button
                                variant="tertiary"
                                size="small"
                                tone="danger"
                                disabled={
                                    isSubmittingIncome || !canMutateIncomes
                                }
                                icon={Trash2Icon}
                                onPress={() => {
                                    onDelete();
                                }}
                            >
                                Delete
                            </Button>
                            <Button
                                variant="primary"
                                size="small"
                                disabled={detailsModalDisabled}
                                onPress={() => {
                                    onSave();
                                }}
                            >
                                Save
                            </Button>
                        </XStack>
                    </>
                )}
            </YStack>
        </GroveSimpleModal>
    );
};
